import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import DataGrid, { DataGridItem } from "components/data-grid";
import { injectIntl } from "react-intl";
import { LinkWithIcon } from "components/link";
import { SecondCoachButtonWithModal } from "components/buttons";
import { TextDisplay } from "components/display-data";
import { useCurrentCustomerContext } from "components/current-customer-context";
import { Restricted, permissions } from "components/auth";
import { ExternalLink } from "components/link";
import Icon from "components/icon";
import withStyles from "components/with-styles";
import styles from "./styles/data.css.js";
import { SimpleButton } from "components/buttons";
import Chip from "components/chip";
import { isPrivacyActivated } from "pages/deployment";

const DeploymentData = ({ id, deployment, intl, classes, onChangePrivacy }) => {
  const { contracts } = useCurrentCustomerContext();

  const fieldConfigUrls = contracts
    ?.filter(c => c.deployment_id === id)
    .map(c => {
      return {
        url: c.field_config_url,
        name: c.name,
        id: c._id,
      };
    });

  return !deployment ? null : (
    <DataGrid>
      <>
        <DataGrid>
          <DataGridItem>
            <DataGridItem big>
              <TextDisplay>{deployment.description}</TextDisplay>
            </DataGridItem>
            <DataGridItem big>
              {deployment.place ? `${deployment.place.name} ${deployment.place.address}` : null}
            </DataGridItem>
          </DataGridItem>
          <DataGridItem threeQuarter>
            {deployment.contacts?.map((contact, index) => (
              <React.Fragment key={index}>
                <DataGridItem big>
                  <TextDisplay>{contact.description}</TextDisplay>
                </DataGridItem>
                <DataGridItem big className={classes.contacts}>
                  {contact.name}
                  {contact.email && ` - ${contact.email}`}
                  {contact.telephone && contact.telephone !== "0"
                    ? ` - ${contact.telephone}`
                    : ` - ${intl.formatMessage({ id: "deployment.contacts.noTelephone" })}`}
                </DataGridItem>
              </React.Fragment>
            ))}
          </DataGridItem>
        </DataGrid>
      </>
      {deployment.proxy_ptz && (
        <>
          <DataGrid>
            <DataGridItem>
              <TextDisplay label={intl.formatMessage({ id: "deployment.proxyPtz.protocol" })}>
                {deployment.proxy_ptz.protocol}
              </TextDisplay>
            </DataGridItem>
            <DataGridItem>
              <TextDisplay label={intl.formatMessage({ id: "deployment.proxyPtz.hostname" })}>
                {deployment.proxy_ptz.hostname}
              </TextDisplay>
            </DataGridItem>
            <DataGridItem>
              <TextDisplay label={intl.formatMessage({ id: "deployment.proxyPtz.port" })}>
                {deployment.proxy_ptz.port}
              </TextDisplay>
            </DataGridItem>
          </DataGrid>
        </>
      )}
      <Restricted permissions={[permissions.deployment_view]}>
        <DataGridItem big>
          <DataGrid>
            <DataGridItem>
              <Chip
                className={cn(classes.privacy, {
                  [classes.privacy__activated]: isPrivacyActivated(deployment.privacy_activated),
                })}
                variant="outlined"
                label={intl.formatMessage({
                  id: `deployment.privacy.in.${deployment.privacy_activated?.toUpperCase()}`,
                })}
              />
            </DataGridItem>
            <DataGridItem small>
              <SimpleButton
                onClick={onChangePrivacy}
                className={classes.privacyButton}
                icon={isPrivacyActivated(deployment.privacy_activated) ? "close" : "check"}
                label={intl.formatMessage({
                  id: `deployment.operations.changePrivacy.in.${
                    isPrivacyActivated(deployment.privacy_activated) ? "deactivate" : "activate"
                  }`,
                })}
              />
            </DataGridItem>
          </DataGrid>
        </DataGridItem>
      </Restricted>
      <DataGridItem big>
        <DataGrid>
          <DataGridItem>
            <LinkWithIcon
              href={`/event/list?deploymentId=${deployment._id}`}
              iconName="event-available"
              text={intl.formatMessage({ id: "deployment.events" })}
            />
          </DataGridItem>
          <DataGridItem>
            <SecondCoachButtonWithModal asButton={false} secondCoachUrl={deployment.second_coach_url} />
          </DataGridItem>
          <DataGridItem>
            <TextDisplay label={intl.formatMessage({ id: "deployment.allowsConcurrentEvents" })}>
              {intl.formatMessage({
                id:
                  deployment.allow_concurrent_events === true
                    ? "common.yes"
                    : deployment.allow_concurrent_events === false
                    ? "common.no"
                    : "common.undetermined",
              })}
            </TextDisplay>
          </DataGridItem>
          <DataGridItem>
            <TextDisplay label={intl.formatMessage({ id: "deployment.allowQod" })}>
              {intl.formatMessage({ id: deployment.allow_qod ? "common.yes" : "common.no" })}
            </TextDisplay>
          </DataGridItem>
          {!!deployment.allow_qod && (
            <DataGridItem>
              <TextDisplay label={intl.formatMessage({ id: "deployment.msisdnQod" })}>
                {deployment.msisdn_qod}
              </TextDisplay>
            </DataGridItem>
          )}
        </DataGrid>
      </DataGridItem>
      {!!deployment?.aws_elastic_ip?.length && (
        <DataGridItem big>
          <TextDisplay label={intl.formatMessage({ id: "deployment.awsElasticIps" })}>
            {deployment.aws_elastic_ip?.map(ip => (
              <DataGridItem big key={ip}>
                {ip}
              </DataGridItem>
            ))}
          </TextDisplay>
        </DataGridItem>
      )}
      <DataGridItem big>
        {fieldConfigUrls?.map(contract => (
          <DataGridItem className={classes.toolLinkContainer} big key={contract.id}>
            <ExternalLink href={contract.url}>
              <Icon name="build" className={classes.toolLinkIcon} />
              {intl.formatMessage({ id: "contract.fieldTool.link" })}
              {fieldConfigUrls.length > 1 ? ": " + contract.name : ""}
            </ExternalLink>
          </DataGridItem>
        ))}
      </DataGridItem>
    </DataGrid>
  );
};

DeploymentData.propTypes = {
  /**
   * Data to represent
   **/
  deployment: PropTypes.object,
  /**
   * Callback que se invoca al cambiar la privacidad
   **/
  onChangePrivacy: PropTypes.func,
};

export default injectIntl(withStyles(styles)(DeploymentData));
